import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert, Autocomplete, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack,
  TextField, Typography,
} from '@mui/material';
import type { Customer, Partner } from '../../api/types';
import { usePartners, useSaveCustomer } from '../../api/hooks';
import { ApiError } from '../../api/client';
import { blankToNull } from '../common/format';
import { useErrorMessage } from '../common/Common';

export function CustomerFormDialog({
  open, customer, onClose, onSaved,
}: {
  open: boolean;
  customer?: Customer | null;
  onClose: () => void;
  onSaved?: (customer: Customer) => void;
}) {
  const { t } = useTranslation();
  const describeError = useErrorMessage();
  const save = useSaveCustomer();
  const { data: partners } = usePartners();

  const [name, setName] = useState('');
  const [website, setWebsite] = useState('');
  const [country, setCountry] = useState('');
  const [notes, setNotes] = useState('');
  const [partner, setPartner] = useState<Partner | null>(null);
  const [touched, setTouched] = useState(false);
  const partnerResolvedFor = useRef<number | null>(null);

  // Reset only when the dialog opens or the target record changes. This must NOT depend on the
  // partner query: its result changes identity when it resolves, and re-running the reset would
  // wipe whatever the user had already typed.
  useEffect(() => {
    if (!open) return;
    setName(customer?.name ?? '');
    setWebsite(customer?.website ?? '');
    setCountry(customer?.country ?? '');
    setNotes(customer?.notes ?? '');
    setPartner(null);
    partnerResolvedFor.current = null;
    setTouched(false);
    save.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, customer]);

  // The partner list can arrive after the dialog is already open, so resolve the initial
  // selection separately — once per opening, so it can never overwrite the user's own choice.
  useEffect(() => {
    if (!open || !partners) return;
    const key = customer?.id ?? 0;
    if (partnerResolvedFor.current === key) return;
    partnerResolvedFor.current = key;
    setPartner(partners.items.find((p) => p.id === customer?.partnerId) ?? null);
  }, [open, customer, partners]);

  const nameMissing = name.trim().length === 0;
  const serverErrors = save.error instanceof ApiError ? save.error.fieldErrors : {};
  const partnerChanged = (partner?.id ?? null) !== (customer?.partnerId ?? null);

  const submit = async () => {
    setTouched(true);
    if (nameMissing) return;
    const saved = await save.mutateAsync({
      id: customer?.id,
      body: {
        name: name.trim(),
        website: blankToNull(website),
        country: blankToNull(country),
        notes: blankToNull(notes),
        partnerId: partner?.id ?? null,
      },
    });
    onSaved?.(saved);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{customer ? t('forms.customer.editTitle') : t('forms.customer.newTitle')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {save.error && !Object.keys(serverErrors).length && (
            <Alert severity="error">{describeError(save.error)}</Alert>
          )}
          <TextField
            label={t('forms.customer.companyName')}
            required
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={() => setTouched(true)}
            error={(touched && nameMissing) || Boolean(serverErrors.name)}
            helperText={touched && nameMissing ? t('forms.customer.nameRequired') : serverErrors.name}
            inputProps={{ dir: 'auto', maxLength: 200, 'data-testid': 'customer-name' }}
          />
          <TextField
            label={t('common.country')}
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            inputProps={{ dir: 'auto', maxLength: 100, 'data-testid': 'customer-country' }}
          />
          <TextField
            label={t('common.website')}
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
            error={Boolean(serverErrors.website)}
            helperText={serverErrors.website}
            inputProps={{ dir: 'auto', maxLength: 400, 'data-testid': 'customer-website' }}
          />

          <Stack spacing={0.5}>
            <Autocomplete
              options={partners?.items ?? []}
              value={partner}
              onChange={(_, value) => setPartner(value)}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('partner.partner')}
                  error={Boolean(serverErrors.partnerId)}
                  helperText={serverErrors.partnerId}
                  inputProps={{ ...params.inputProps, 'data-testid': 'customer-partner-input' }}
                />
              )}
              data-testid="customer-partner"
            />
            {/* Only shown when it is actually about to happen — a standing caption is noise. */}
            {customer && partnerChanged && (
              <Typography variant="caption" color="text.secondary" data-testid="partner-propagation-notice">
                {t('partner.propagationNotice')}
              </Typography>
            )}
          </Stack>

          <TextField
            label={t('common.notes')}
            multiline
            minRows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            inputProps={{ dir: 'auto', 'data-testid': 'customer-notes' }}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={save.isPending}>{t('common.cancel')}</Button>
        <Button variant="contained" onClick={submit} disabled={save.isPending} data-testid="save-customer">
          {t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
